import React, { useState } from 'react';

import { createPortal } from 'react-dom';

import { classnames, color, margin } from '@lumapps/classnames';
import { DndContext, SortableContext, DragOverlay } from '@lumapps/lumx-dnd/components';
import { useSensor, useSensors } from '@lumapps/lumx-dnd/hooks';
import type { UniqueIdentifier, DragStartEvent, DragEndEvent } from '@lumapps/lumx-dnd/types';
import {
    arrayMove,
    closestCenter,
    defaultDropAnimationSideEffects,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    rectSortingStrategy,
    restrictToHorizontalAxis,
    restrictToVerticalAxis,
    sortableKeyboardCoordinates,
    TouchSensor,
} from '@lumapps/lumx-dnd/utils';
import { Heading, Table, TableBody } from '@lumapps/lumx/react';

import { useSortableAccessibilityInstructions } from '../../hooks/useSortableAccessibilityInstructions';
import { SortableListContainer } from './SortableListContainer';
import { SortableListItem } from './SortableListItem';
import { SortableListProps } from './types';

const lockedAxisModifiersMap = {
    vertical: [restrictToVerticalAxis],
    horizontal: [restrictToHorizontalAxis],
    grid: [],
    table: [restrictToVerticalAxis],
};

export const SortableList = <T,>({
    afterItems,
    className,
    columns,
    entities,
    gap,
    isDisabled = false,
    label,
    mode = 'vertical',
    useDragOverlay = false,
    withDragHandle = false,
    withDirectMovement = false,
    withLockedAxis = false,
    entityRenderer,
    isEntityDisabled,
    onEnter,
    onFocus,
    onMove,
}: SortableListProps<T>) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
            // Don't use enter key to pick up an item if onEnter is defined
            keyboardCodes: {
                start: ['Space', ...(!onEnter ? ['Enter'] : [])],
                cancel: ['Escape'],
                end: ['Space', ...(!onEnter ? ['Enter'] : [])],
            },
        }),
    );

    const [items, setItems] = useState<UniqueIdentifier[]>(() => entities.map((_, index) => index));
    const [active, setActive] = useState<{ id: UniqueIdentifier; index: number } | null>(null);
    const containerRef = React.useRef<(HTMLOListElement & HTMLTableSectionElement) | null>(null);
    const itemRefs = React.useRef<(HTMLLIElement | HTMLTableRowElement | null)[]>([]);

    const getEntityIndex = React.useCallback((id: UniqueIdentifier) => items.indexOf(id), [items]);

    const handleDragStart = React.useCallback(
        (event: DragStartEvent) => {
            const { active } = event;
            if (active) {
                setActive({ id: active.id, index: getEntityIndex(active.id) });
            }
        },
        [getEntityIndex],
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                const oldIndex = getEntityIndex(active.id);
                const newIndex = getEntityIndex(over.id);
                onMove(arrayMove(entities, oldIndex, newIndex), oldIndex, newIndex);
                setItems((items) => arrayMove(items, oldIndex, newIndex));
            }
            setActive(null);
        },
        [entities, getEntityIndex, onMove],
    );

    const handleDragCancel = React.useCallback(() => {
        setActive(null);
    }, []);

    const measuring = React.useMemo(
        () => ({
            droppable: {
                strategy: MeasuringStrategy.Always,
                frequency: 5,
            },
        }),
        [],
    );

    const accessibility = useSortableAccessibilityInstructions<T>({
        containerRef,
        mode,
        entities,
        active,
        getEntityIndex,
    });

    const modifiers = React.useMemo(() => {
        if (!withLockedAxis) {
            return [];
        }
        return lockedAxisModifiersMap[mode];
    }, [mode, withLockedAxis]);

    // Update items when entities are added/removed
    React.useEffect(() => {
        if (entities.length !== items.length) {
            setItems(entities.map((_, index) => index));
        }
    }, [entities, items.length]);

    const Preview = React.useCallback(() => {
        if (active == null) {
            return null;
        }
        const item = (
            <SortableListItem
                key={active.id}
                id={active.id}
                index={active.index}
                entity={entities[active.index]}
                className={className}
                isDragged
                gap={gap}
                mode={mode}
                columns={columns}
                isDisabled={isEntityDisabled?.(entities[active.index]) || isDisabled}
                entityRenderer={entityRenderer}
                withDragHandle={withDragHandle}
                withDirectMovement={withDirectMovement}
            />
        );
        if (mode === 'table') {
            return (
                <Table>
                    <TableBody>{item}</TableBody>
                </Table>
            );
        }
        return item;
    }, [
        active,
        className,
        columns,
        entities,
        entityRenderer,
        gap,
        isDisabled,
        isEntityDisabled,
        mode,
        withDirectMovement,
        withDragHandle,
    ]);

    return (
        <>
            {label ? (
                <Heading
                    as="h3"
                    className={classnames(
                        'lumx-text-field__label',
                        isDisabled && color('font', 'dark', 'L3'),
                        margin('bottom'),
                    )}
                >
                    {label} {isDisabled ? 'disabled' : 'enabled'}
                </Heading>
            ) : null}
            <DndContext
                accessibility={accessibility}
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
                modifiers={modifiers}
                measuring={measuring}
            >
                <SortableContext items={items} strategy={rectSortingStrategy}>
                    <SortableListContainer
                        mode={mode}
                        columns={columns}
                        gap={gap}
                        containerRef={containerRef}
                        itemRefs={itemRefs}
                        className={className}
                    >
                        {items.map((id: UniqueIdentifier) => {
                            const index = items.indexOf(id);
                            return (
                                <SortableListItem
                                    key={id}
                                    id={id}
                                    index={index}
                                    entity={entities[index]}
                                    className={className}
                                    isDragged={active?.id === id && withDirectMovement}
                                    gap={gap}
                                    mode={mode}
                                    columns={columns}
                                    onEnter={onEnter}
                                    onFocus={onFocus}
                                    isDisabled={isEntityDisabled?.(entities[index]) || isDisabled}
                                    entityRenderer={entityRenderer}
                                    withDragHandle={withDragHandle}
                                    withDirectMovement={withDirectMovement}
                                    itemRef={(el) => {
                                        itemRefs.current[index] = el;
                                    }}
                                />
                            );
                        })}
                        {afterItems}
                    </SortableListContainer>
                </SortableContext>
                {useDragOverlay || !withDirectMovement
                    ? createPortal(
                          <DragOverlay
                              adjustScale={false}
                              dropAnimation={{
                                  sideEffects: defaultDropAnimationSideEffects({
                                      styles: {
                                          active: {
                                              opacity: '0.5',
                                          },
                                      },
                                  }),
                              }}
                          >
                              <Preview />
                          </DragOverlay>,
                          document.body,
                      )
                    : null}
            </DndContext>
        </>
    );
};
